import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import NotificationBell from "./NotificationBell";
import { LevelBadge, SportLevelBadge, MannerBadge } from "./Badges";
import ClubRecruitment from "./ClubRecruitment";
import pickleballIcon from "./pickleball.png";

const SPORTS = {
  all:         { icon: "🏆", label: "전체" },
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const SEOUL_DISTRICTS = [
  "종로구","중구","용산구","성동구","광진구","동대문구","중랑구","성북구",
  "강북구","도봉구","노원구","은평구","서대문구","마포구","양천구","강서구",
  "구로구","금천구","영등포구","동작구","관악구","서초구","강남구","송파구","강동구",
  "수원시","성남시","용인시","부천시","안산시","안양시","남양주시","화성시",
  "평택시","의정부시","시흥시","파주시","광명시","김포시","광주시","군포시",
  "하남시","오산시","양주시","고양시",
  "인천 남동구","인천 부평구","인천 연수구","인천 서구",
];

const SKILL_LEVELS = ["전체","입문","초급","중급","고급"];
const WEEK_DAYS     = ["일","월","화","수","목","금","토"];

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

function formatGameDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric" }) + " (" + WEEK_DAYS[d.getDay()] + ")";
}

function buildLocation(post) {
  return [post.location_city, post.location_district, post.location_dong, post.location_name]
    .filter(Boolean).join(" ") || "-";
}

const HOME_CACHE_KEY = "cp_home_cache_v1";
function readHomeCache() {
  try { return JSON.parse(localStorage.getItem(HOME_CACHE_KEY)) || null; }
  catch (e) { return null; }
}
function writeHomeCache(patchObj) {
  try {
    const prev = readHomeCache() || {};
    localStorage.setItem(HOME_CACHE_KEY, JSON.stringify({ ...prev, ...patchObj }));
  } catch (e) {}
}

export default function MatchList({ onTeamClick, onPostClick, onCreatePost, onEditPost, onHome, onClubTab, initialInnerTab, hideInnerTabs, onBack, pageTitle, blockedTeamIds = [], onGuide, initialBanners, initialBannerInterval, initialPosts, initialMatchedPostIds }) {
  const { user, activeTeam } = useAuth();
  // 내 팀장 팀 id 목록 + 내 팀원 팀 id 목록 (수정/삭제 권한 판단용)
  const [myLeaderIds, setMyLeaderIds] = useState(new Set());
  const [myMemberIds, setMyMemberIds] = useState(new Set());
  const [innerTab, setInnerTab] = useState(initialInnerTab || "posts");

  useEffect(() => {
    if (initialInnerTab) setInnerTab(initialInnerTab);
  }, [initialInnerTab]);
  const [clubRefreshKey, setClubRefreshKey] = useState(0);
  const [openFilter, setOpenFilter] = useState(null);

  const [teams,    setTeams]    = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  const [sportFilter,  setSportFilter]  = useState("all");
  const [regionFilter, setRegionFilter] = useState("");
  const [skillFilter,  setSkillFilter]  = useState("");
  const [search,       setSearch]       = useState("");
  const [sortOrder,    setSortOrder]    = useState("latest"); // "latest" | "matchDate" | "deadline"

  const _homeCache = readHomeCache();
  const [posts,         setPosts]         = useState(initialPosts || (_homeCache && _homeCache.posts) || []);
  const [postsLoading,  setPostsLoading]  = useState(false);
  const [matchedPostIds, setMatchedPostIds] = useState(initialMatchedPostIds || new Set((_homeCache && _homeCache.matchedPostIds) || []));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [banners,        setBanners]        = useState(initialBanners || (_homeCache && _homeCache.banners) || []);
  const [bannerIdx,      setBannerIdx]      = useState(0);
  const [bannerInterval, setBannerInterval] = useState(initialBannerInterval || (_homeCache && _homeCache.bannerInterval) || 4000);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd,   setTouchEnd]   = useState(null);

  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  // 배너 조회 + 슬라이드 인터벌 설정 불러오기 (프리페치 데이터 있으면 스킵)
  useEffect(() => {
    if (initialBanners != null) return;
    Promise.all([
      supabase.from("banners").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
      supabase.from("app_settings").select("value").eq("key", "banner_interval").single(),
    ]).then(([{ data: bannersData }, { data: settingData }]) => {
      setBanners(bannersData || []);
      if (settingData) setBannerInterval((Number(settingData.value) || 4) * 1000);
    });
  }, []); // eslint-disable-line

  // 배너 자동 슬라이드
  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => {
      setBannerIdx(prev => (prev + 1) % banners.length);
    }, bannerInterval);
    return () => clearInterval(timer);
  }, [banners.length, bannerInterval]);

  // 캐시 우선: 배너/인터벌 로드되면 다음 실행을 위해 저장
  useEffect(() => {
    if (banners && banners.length) writeHomeCache({ banners, bannerInterval });
  }, [banners, bannerInterval]);

  const handleBannerClick = async (banner) => {
    if (!banner.link_value) return;
    if (banner.link_type === "external") {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: banner.link_value });
      } else {
        window.open(banner.link_value, "_blank");
      }
    }
  };

  const onBannerTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onBannerTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onBannerTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50)  setBannerIdx(prev => (prev + 1) % banners.length);
    if (distance < -50) setBannerIdx(prev => (prev - 1 + banners.length) % banners.length);
  };

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from("teams").select("*").order("created_at", { ascending: false });
    if (error) setError("팀 목록을 불러오지 못했어요: " + error.message);
    else { setTeams(data || []); setFiltered(data || []); }
    setLoading(false);
  }, []);

  useEffect(() => { if (innerTab === "teams") fetchTeams(); }, [innerTab, fetchTeams]);

  // 내 팀장/팀원 관계 로드
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      supabase.from("teams").select("id").eq("user_id", user.id),
      supabase.from("team_members").select("team_id").eq("user_id", user.id).eq("status", "accepted"),
    ]).then(([{ data: leaderTeams }, { data: memberships }]) => {
      setMyLeaderIds(new Set((leaderTeams || []).map(t => t.id)));
      setMyMemberIds(new Set((memberships || []).map(m => m.team_id)));
    });
  }, [user?.id]); // eslint-disable-line

  useEffect(() => {
    let result = [...teams];
    if (blockedTeamIds.length) result = result.filter((t) => !blockedTeamIds.includes(t.id));
    if (sportFilter !== "all") result = result.filter((t) => t.sports_type === sportFilter);
    if (regionFilter)          result = result.filter((t) => t.region === regionFilter);
    if (skillFilter)           result = result.filter((t) => t.skill_level === skillFilter);
    if (search.trim())         result = result.filter((t) => t.team_name?.toLowerCase().includes(search.toLowerCase()));
    setFiltered(result);
  }, [sportFilter, regionFilter, skillFilter, search, teams, blockedTeamIds]);

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    const today = new Date().toISOString().split("T")[0];
    const { data } = await supabase.from("posts").select("id, team_id, sports_type, deadline, team_name, desired_level, game_date, status, updated_at, created_at, location_city, location_district, location_dong, location_name")
      .eq("status", "open")
      .or("deadline.is.null,deadline.gte." + today)
      .order("updated_at", { ascending: false, nullsFirst: false })
      .order("created_at",  { ascending: false });
    const visible = (data || []).filter((p) => !p.game_date || p.game_date >= today);
    setPosts(visible);
    const postIds = visible.map(p => p.id);
    let matchedIds = [];
    if (postIds.length > 0) {
      const { data: acceptedApps } = await supabase
        .from("applications")
        .select("post_id")
        .in("post_id", postIds)
        .eq("status", "accepted");
      matchedIds = (acceptedApps || []).map(a => a.post_id);
    }
    setMatchedPostIds(new Set(matchedIds));
    writeHomeCache({ posts: visible, matchedPostIds: matchedIds });
    setPostsLoading(false);
  }, []);

  useEffect(() => {
    // 캐시 우선: 저장된 목록을 즉시 보여주고, 최신 데이터는 백그라운드로 새로고침
    if (innerTab === "posts") fetchPosts();
  }, [innerTab, fetchPosts]); // eslint-disable-line

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      if (innerTab === "club") setClubRefreshKey((k) => k + 1);
      else { await Promise.all([fetchTeams(), fetchPosts()]); }
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY, innerTab, fetchTeams, fetchPosts]);

  const handleDeletePost = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    await supabase.from("applications").delete().eq("post_id", confirmDelete.id);
    await supabase.from("posts").delete().eq("id", confirmDelete.id);
    setPosts((prev) => prev.filter((p) => p.id !== confirmDelete.id));
    setDeleteLoading(false);
    setConfirmDelete(null);
  };

  const filteredPosts = posts.filter((p) => {
    if (blockedTeamIds.length && blockedTeamIds.includes(p.team_id)) return false;
    if (sportFilter !== "all" && p.sports_type !== sportFilter) return false;
    if (search.trim() && !p.team_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (regionFilter && p.location_district !== regionFilter) return false;
    if (skillFilter && p.desired_level !== skillFilter) return false;
    return true;
  });

  // ── 정렬 ──
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const sortedPosts = sortOrder === "latest"
    ? [...filteredPosts]
    : [...filteredPosts].sort((a, b) => {
        if (sortOrder === "matchDate") {
          const aDate = a.game_date ? new Date(a.game_date) : null;
          const bDate = b.game_date ? new Date(b.game_date) : null;
          const aFuture = aDate && aDate >= todayDate;
          const bFuture = bDate && bDate >= todayDate;
          if (aFuture && bFuture) return aDate - bDate;
          if (aFuture) return -1;
          if (bFuture) return 1;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return aDate - bDate;
        }
        // deadline
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline) - new Date(b.deadline);
      });

  const totalMembers   = (t) => (t.male_count || 0) + (t.female_count || 0);
  const hasExtraFilter = regionFilter || skillFilter || sportFilter !== "all";
  const toggleFilter   = (key) => setOpenFilter((prev) => (prev === key ? null : key));

  const sportLabel  = sportFilter === "all" ? "종목" : (SPORTS[sportFilter]?.label || "종목");
  const regionLabel = regionFilter || "지역";
  const skillLabel  = skillFilter  || "레벨";

  if (loading && innerTab === "teams") {
    return (
      <div style={styles.container}>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <p style={{ color: "#aaa", fontSize: 14, marginTop: 16 }}>팀 목록 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ ...styles.container, overflowY: "auto" }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {/* Pull to Refresh 인디케이터 */}
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#f7f8fa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...styles.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}
      {/* 헤더 */}
      {hideInnerTabs && onBack ? (
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{pageTitle || "팀 찾기"}</span>
          </div>
        </div>
      ) : (
        <div style={styles.headerSlim}>
          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", left: 0 }}>
              {onGuide && (
                <button
                  onClick={onGuide}
                  style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer", padding: "3px 7px", fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}
                >
                  📖 사용법
                </button>
              )}
            </div>
            <div style={styles.headerTitle}><span style={{ color: "#E24B4A", fontWeight: 800 }}>Club</span> 교류전 매칭</div>
            <div style={{ position: "absolute", right: 0, display: "flex", alignItems: "center", gap: 2 }}>
              <NotificationBell />
            </div>
          </div>
        </div>
      )}

      {/* 내부 탭 — hideInnerTabs면 숨김 */}
      {!hideInnerTabs && initialInnerTab ? (
        <div style={styles.innerTabRow}>
          {[["posts","교류전 모집"],["teams","팀 찾기"],["club","회원 모집"]].map(([key, label]) => (
            <button key={key}
              style={{ ...styles.innerTab, borderBottom: innerTab === key ? "2px solid #E24B4A" : "2px solid transparent", color: innerTab === key ? "#E24B4A" : "#9ca3af" }}
              onClick={() => { setInnerTab(key); setOpenFilter(null); }}
            >{label}</button>
          ))}
        </div>
      ) : null}

      {innerTab === "club" && <ClubRecruitment onHome={onHome} parentRefreshKey={clubRefreshKey} />}

      {/* 검색창 */}
      {innerTab !== "club" && (
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>🔍</span>
          <input style={styles.searchInput} placeholder="팀 이름으로 검색..." value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button style={styles.clearBtn} onClick={() => setSearch("")}>✕</button>}
        </div>
      )}

      {/* 배너 캐러셀 */}
      {innerTab !== "club" && banners.length > 0 && (
        <div style={{ margin: "10px 20px 0", position: "relative" }}>
          <div
            style={{
              aspectRatio: "16 / 6",
              borderRadius: 12,
              overflow: "hidden",
              cursor: banners[bannerIdx]?.link_value ? "pointer" : "default",
            }}
            onClick={() => handleBannerClick(banners[bannerIdx])}
            onTouchStart={onBannerTouchStart}
            onTouchMove={onBannerTouchMove}
            onTouchEnd={onBannerTouchEnd}
          >
            {banners.map((b, i) => (
              <img
                key={b.id}
                src={b.image_url}
                alt="배너"
                loading={i === 0 ? "eager" : "lazy"}
                fetchpriority={i === 0 ? "high" : "auto"}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: i === bannerIdx ? 1 : 0,
                  transition: "opacity 0.4s ease",
                }}
              />
            ))}
          </div>
          {banners.length > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 6 }}>
              {banners.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setBannerIdx(i)}
                  style={{
                    width: i === bannerIdx ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: i === bannerIdx ? "#E8412A" : "#d1d5db",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 필터 버튼 한 줄 */}
      {innerTab !== "club" && (
        <div style={styles.filterBtnRow}>
          <button style={{ ...styles.filterPill, borderColor: sportFilter !== "all" ? "#E24B4A" : "#e5e7eb", color: sportFilter !== "all" ? "#E24B4A" : "#6b7280", fontWeight: sportFilter !== "all" ? 700 : 500 }} onClick={() => toggleFilter("sport")}>
            {SPORTS[sportFilter]?.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: "1em", height: "1em", verticalAlign: "middle", objectFit: "contain" }} />} {sportLabel} {openFilter === "sport" ? "▲" : "▼"}
          </button>
          <button style={{ ...styles.filterPill, borderColor: regionFilter ? "#E24B4A" : "#e5e7eb", color: regionFilter ? "#E24B4A" : "#6b7280", fontWeight: regionFilter ? 700 : 500 }} onClick={() => toggleFilter("region")}>
            📍 {regionLabel} {openFilter === "region" ? "▲" : "▼"}
          </button>
          <button style={{ ...styles.filterPill, borderColor: skillFilter ? "#E24B4A" : "#e5e7eb", color: skillFilter ? "#E24B4A" : "#6b7280", fontWeight: skillFilter ? 700 : 500 }} onClick={() => toggleFilter("skill")}>
            {skillLabel} {openFilter === "skill" ? "▲" : "▼"}
          </button>
          <div style={{ flexShrink: 0, width: 12 }} />
        </div>
      )}

      {/* 종목 패널 */}
      {innerTab !== "club" && openFilter === "sport" && (
        <div style={styles.filterPanel}>
          <div style={styles.filterScroll}>
            {Object.entries(SPORTS).map(([key, sp]) => (
              <button key={key}
                style={{ ...styles.filterBtn, borderColor: sportFilter === key ? "#E24B4A" : "#e5e7eb", background: sportFilter === key ? "#E24B4A" : "#fff", color: sportFilter === key ? "#fff" : "#6b7280" }}
                onClick={() => { setSportFilter(key); setOpenFilter(null); }}
              >
                <span style={{ fontSize: 16 }}>{sp.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 16, height: 16, objectFit: "contain" }} />}</span>
                <span style={{ fontSize: 10 }}>{sp.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 지역 패널 */}
      {innerTab !== "club" && openFilter === "region" && (
        <div style={styles.filterPanel}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 0" }}>
            <button style={{ ...styles.filterChip, borderColor: !regionFilter ? "#E24B4A" : "#e5e7eb", background: !regionFilter ? "#E24B4A" : "#fff", color: !regionFilter ? "#fff" : "#6b7280" }}
              onClick={() => { setRegionFilter(""); setOpenFilter(null); }}>전체</button>
            {SEOUL_DISTRICTS.map((d) => (
              <button key={d}
                style={{ ...styles.filterChip, borderColor: regionFilter === d ? "#E24B4A" : "#e5e7eb", background: regionFilter === d ? "#E24B4A" : "#fff", color: regionFilter === d ? "#fff" : "#6b7280" }}
                onClick={() => { setRegionFilter(d); setOpenFilter(null); }}>{d}</button>
            ))}
          </div>
        </div>
      )}

      {/* 레벨 패널 */}
      {innerTab !== "club" && openFilter === "skill" && (
        <div style={styles.filterPanel}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 0" }}>
            {SKILL_LEVELS.map((lv) => {
              const val = lv === "전체" ? "" : lv;
              const active = skillFilter === val;
              return (
                <button key={lv}
                  style={{ ...styles.filterChip, borderColor: active ? "#E24B4A" : "#e5e7eb", background: active ? "#E24B4A" : "#fff", color: active ? "#fff" : "#6b7280" }}
                  onClick={() => { setSkillFilter(val); setOpenFilter(null); }}>{lv}</button>
              );
            })}
          </div>
        </div>
      )}

      {/* 필터 초기화 */}
      {innerTab !== "club" && hasExtraFilter && (
        <div style={{ padding: "0 20px 6px", display: "flex", justifyContent: "flex-end" }}>
          <button style={{ fontSize: 12, color: "#E24B4A", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
            onClick={() => { setSportFilter("all"); setRegionFilter(""); setSkillFilter(""); setOpenFilter(null); }}>
            필터 초기화 ✕
          </button>
        </div>
      )}

      {/* 결과 수 + 정렬 (posts 탭) */}
      {innerTab !== "club" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 10px", fontSize: 13 }}>
          <div>
            <span style={{ color: "#E8412A", fontWeight: 700 }}>{innerTab === "teams" ? filtered.length : filteredPosts.length}</span>
            <span style={{ color: "#9ca3af" }}>{innerTab === "teams" ? "개 팀 검색됨" : "개 모집글"}</span>
          </div>
          {innerTab === "posts" && (
            <div style={{ display: "flex", gap: 4 }}>
              {[["latest", "최신순"], ["matchDate", "경기임박순"], ["deadline", "마감임박순"]].map(([key, label]) => (
                <button key={key}
                  onClick={() => setSortOrder(key)}
                  style={{ padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    background: sortOrder === key ? "#1A1A2E" : "#fff",
                    color: sortOrder === key ? "#fff" : "#6b7280",
                    border: sortOrder === key ? "1px solid #1A1A2E" : "1px solid #d1d5db",
                  }}
                >{label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {error && innerTab === "teams" && (
        <div style={styles.errorBox}><span>⚠️</span><span style={{ marginLeft: 8, fontSize: 13, color: "#dc2626" }}>{error}</span></div>
      )}

      {/* 팀 찾기 탭 */}
      {innerTab === "teams" && (
        <div style={styles.body}>
          {filtered.length === 0 ? (
            <div style={styles.emptyWrap}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏟️</div>
              <div style={{ fontSize: 16, color: "#9ca3af", fontWeight: 600 }}>검색 결과가 없어요</div>
              <div style={{ fontSize: 13, color: "#c4c9d4", marginTop: 6 }}>다른 종목이나 검색어로 찾아보세요</div>
            </div>
          ) : (
            filtered.map((team) => (
              <TeamCard key={team.team_id || team.id} team={team} totalMembers={totalMembers(team)} onClick={() => onTeamClick(team)} />
            ))
          )}
        </div>
      )}

      {/* 교류전 모집 탭 */}
      {innerTab === "posts" && (
        <div style={styles.body}>
          <button
            style={styles.createPostBtn}
            onClick={() => {
              if (!activeTeam) {
                alert("먼저 내 정보 탭에서 팀을 등록하거나 선택해주세요.");
                return;
              }
              onCreatePost();
            }}
          >
            ✏️ 교류전 모집글 작성하기
          </button>
          {postsLoading && posts.length === 0 ? (
            <div style={styles.loadingWrap}><div style={styles.spinner} /></div>
          ) : filteredPosts.length === 0 ? (
            <div style={styles.emptyWrap}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 16, color: "#9ca3af", fontWeight: 600 }}>모집글이 없어요</div>
              <div style={{ fontSize: 13, color: "#c4c9d4", marginTop: 6 }}>첫 번째 교류전 모집글을 작성해보세요!</div>
            </div>
          ) : (
            sortedPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                isLeader={myLeaderIds.has(post.team_id)}
                isMember={!myLeaderIds.has(post.team_id) && myMemberIds.has(post.team_id)}
                isMatched={matchedPostIds.has(post.id)}
                onClick={() => onPostClick(post)}
                onEdit={() => onEditPost(post)}
                onDelete={() => setConfirmDelete(post)}
              />
            ))
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDeletePostModal onConfirm={handleDeletePost} onClose={() => setConfirmDelete(null)} loading={deleteLoading} />
      )}
    </div>
  );
}

function TeamCard({ team, totalMembers, onClick }) {
  const sport = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type };
  return (
    <div style={styles.card} onClick={onClick}>
      <div style={styles.cardTop}>
        <div style={styles.sportBadge}>
          {team.logo_url
            ? <img src={team.logo_url} alt="logo" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 13 }} />
            : <span style={{ fontSize: 22 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.teamName}>{team.team_name}</div>
          <div style={styles.sportLabel}>{sport.label}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <SportLevelBadge level={team.level} label="자체평가" />
          <LevelBadge level={team.match_level} compact />
          <MannerBadge score={team.manner_avg} compact />
        </div>
      </div>
      <div style={styles.cardMeta}>
        {team.region && <MetaChip icon="📍" text={team.region} />}
        {team.skill_level && <MetaChip icon="⭐" text={team.skill_level} />}
        {totalMembers > 0 && <MetaChip icon="👥" text={totalMembers + "명"} />}
        {team.age_group && <MetaChip icon="🎂" text={parseAgeGroup(team.age_group)} />}
      </div>
    </div>
  );
}

function PostCard({ post, isLeader, isMember, isMatched, onClick, onEdit, onDelete }) {
  const sport    = SPORTS[post.sports_type] || { icon: "🏅", label: post.sports_type };
  const location = buildLocation(post);
  const daysLeft = post.deadline
    ? Math.ceil((new Date(post.deadline) - new Date()) / 86400000)
    : null;
  return (
    <div style={styles.card} onClick={onClick}>
      <div style={styles.cardTop}>
        <div style={styles.sportBadge}>
          {post.logo_url
            ? <img src={post.logo_url} alt="logo" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 13 }} />
            : <span style={{ fontSize: 22 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}</span>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.teamName}>{post.team_name}</div>
          <div style={styles.sportLabel}>{sport.label}</div>
        </div>
        {isMatched && (
          <div style={styles.matchedBadge}>✅ 매칭완료</div>
        )}
        {!isMatched && post.desired_level && post.desired_level !== "무관" && (
          <div style={styles.levelBadge}>{(() => { try { const arr = JSON.parse(post.desired_level); return Array.isArray(arr) ? arr.join(", ") : post.desired_level; } catch { return post.desired_level; } })()} 희망</div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, margin: "6px 0 6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <span style={{ color: "#E8412A", fontWeight: 700 }}>📅</span>
          <span style={{ color: "#E8412A", fontWeight: 700 }}>{formatGameDate(post.game_date)}</span>
        </div>
        {location !== "-" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
            <span>📍</span><span>{location}</span>
          </div>
        )}
      </div>
      {isLeader ? (
        // 내가 팀장인 팀의 글 → 상세보기 + 수정 + 삭제
        <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
          <button style={styles.postEditBtn} onClick={(e) => { e.stopPropagation(); onClick(); }}>📋 상세보기</button>
          <button style={styles.postEditBtn} onClick={(e) => { e.stopPropagation(); onEdit(); }}>✏️ 수정</button>
          <button style={{ ...styles.postEditBtn, color: "#E24B4A", borderColor: "#fecaca", background: "#fff5f5" }} onClick={(e) => { e.stopPropagation(); onDelete(); }}>🗑 삭제</button>
        </div>
      ) : isMember ? (
        // 내가 팀원(비팀장)인 팀의 글 → 상세보기만
        <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
          <button style={styles.postEditBtn} onClick={(e) => { e.stopPropagation(); onClick(); }}>📋 상세보기</button>
        </div>
      ) : (
        // 남의 팀 글 → 신청하기(또는 매칭완료) + 마감일
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {isMatched ? (
            <div style={{ background: "#f3f4f6", color: "#9ca3af", fontSize: 13, fontWeight: 700, borderRadius: 8, padding: "8px 20px", cursor: "default", display: "inline-block" }}>매칭완료</div>
          ) : (
            <div style={{ background: "#fff", border: "1.5px solid #E24B4A", color: "#E24B4A", fontSize: 13, fontWeight: 700, borderRadius: 8, padding: "8px 20px", cursor: "pointer", display: "inline-block" }}>신청하기</div>
          )}
          {!isMatched && daysLeft !== null && (
            <div style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: daysLeft <= 2 ? "#fff5f5" : "#f5f6fa", color: daysLeft <= 2 ? "#E24B4A" : "#9ca3af", border: "1px solid " + (daysLeft <= 2 ? "#fecaca" : "#e5e7eb") }}>
              {daysLeft === 0 ? "오늘 마감" : daysLeft === 1 ? "내일 마감" : daysLeft + "일 후 마감"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmDeletePostModal({ onConfirm, onClose, loading }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "28px 24px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>모집글을 삭제하시겠어요?</div>
          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>삭제하면 해당 모집글에 신청한 내용도<br />모두 사라집니다.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ flex: 1, padding: "12px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" }} onClick={onClose} disabled={loading}>취소</button>
          <button style={{ flex: 1, padding: "12px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.6 : 1 }} onClick={onConfirm} disabled={loading}>{loading ? "삭제 중..." : "삭제하기"}</button>
        </div>
      </div>
    </div>
  );
}

function MetaChip({ icon, text }) {
  return (
    <div style={styles.metaChip}>
      <span style={{ fontSize: 11 }}>{icon}</span>
      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 3 }}>{text}</span>
    </div>
  );
}

const styles = {
  container:    { minHeight: "100vh", background: "#f7f8fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:       { padding: "calc(env(safe-area-inset-top) + 14px) 20px 14px", borderBottom: "1px solid #edf0f3", background: "#ffffff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
  headerSlim:   { padding: "calc(env(safe-area-inset-top) + 12px) 20px 12px", borderBottom: "1px solid #edf0f3", background: "#ffffff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
  headerTitle:  { fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  innerTabRow:  { display: "flex", background: "#fff", borderBottom: "1px solid #edf0f3" },
  innerTab:     { flex: 1, padding: "12px 0", background: "none", border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "color 0.15s" },
  searchWrap:   { display: "flex", alignItems: "center", margin: "14px 20px 0", background: "#ffffff", borderRadius: 12, border: "1px solid #edf0f3", padding: "0 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  searchIcon:   { fontSize: 14, marginRight: 8, opacity: 0.45 },
  searchInput:  { flex: 1, background: "transparent", border: "none", color: "#111827", fontSize: 14, padding: "13px 0", outline: "none" },
  clearBtn:     { background: "none", border: "none", color: "#c4c9d4", fontSize: 12, cursor: "pointer", padding: "4px" },
  filterBtnRow: { display: "flex", gap: 8, padding: "12px 0 6px 20px", overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", msOverflowStyle: "none" },
  filterPill:   { display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", border: "1.5px solid #e5e7eb", borderRadius: 999, background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.15s" },
  filterPanel:  { padding: "0 20px 4px", borderBottom: "1px solid #edf0f3", background: "#fafafa" },
  filterScroll: { display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 0" },
  filterBtn:    { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "7px 4px", border: "1px solid #edf0f3", borderRadius: 10, background: "#fff", cursor: "pointer", transition: "all 0.15s", flex: "0 0 calc(25% - 4.5px)" },
  filterChip:   { padding: "7px 14px", fontSize: 12, fontWeight: 600, border: "1px solid #edf0f3", borderRadius: 999, background: "#fff", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" },
  resultCount:  { display: "flex", alignItems: "center", gap: 4, padding: "8px 20px 10px", fontSize: 13 },
  errorBox:     { display: "flex", alignItems: "center", margin: "0 20px 12px", padding: "12px 16px", background: "#fff5f5", borderRadius: 12, border: "1px solid #fecaca" },
  body:         { flex: 1, padding: "0 20px 36px", display: "flex", flexDirection: "column", gap: 6 },
  createPostBtn:{ padding: "14px", background: "#111827", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 4 },
  card:         { background: "#ffffff", borderRadius: 12, border: "1px solid #edf0f3", padding: "10px 12px", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", transition: "box-shadow 0.15s" },
  cardTop:      { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 },
  sportBadge:   { width: 38, height: 38, background: "#f7f8fa", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #edf0f3", flexShrink: 0, overflow: "hidden" },
  teamName:     { fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 3 },
  sportLabel:   { fontSize: 12, color: "#b0b8c4" },
  levelBadge:   { padding: "4px 12px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#E24B4A", flexShrink: 0 },
  matchedBadge: { padding: "3px 8px", background: "#1A1A2E", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 6, flexShrink: 0 },
  skillBadge:   { padding: "4px 10px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#0369a1", flexShrink: 0 },
  cardMeta:     { display: "flex", flexWrap: "wrap", gap: 6 },
  metaChip:     { display: "flex", alignItems: "center", padding: "4px 10px", background: "#f7f8fa", borderRadius: 999, border: "1px solid #edf0f3" },
  postEditBtn:  { flex: 1, padding: "8px 4px", background: "#f7f8fa", color: "#374151", border: "1px solid #edf0f3", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  emptyWrap:    { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" },
  loadingWrap:  { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "200px" },
  spinner:      { width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};
